import { makeStyles } from "@material-ui/core";
import React from "react";
import { useHistory } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { tokens } from "../theme/tokens";

const useStyle = makeStyles(() => ({
  table: {
    width: "100%",
    minWidth: "1040px",
    borderCollapse: "collapse",
    textAlign: "left",
    fontVariantNumeric: "tabular-nums",
  },
  th: {
    backgroundColor: tokens.color.surface.sunken,
    textAlign: "left",
    fontSize: tokens.type.caption.size,
    fontWeight: tokens.type.denseStrong.weight,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: tokens.color.text.secondary,
    padding: `${tokens.space[2]} ${tokens.space[3]}`,
    whiteSpace: "nowrap",
    borderBottom: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  },
  row: {
    cursor: "pointer",
    transition: `background-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:hover": { backgroundColor: tokens.color.surface.hover },
    "&:hover $chev": { opacity: 1, transform: "translateX(0)" },
    "&:focus-visible": {
      outline: `${tokens.focus.width} solid ${tokens.color.focus.ring}`,
      outlineOffset: `-${tokens.focus.width}`,
    },
    "&:last-child $td": { borderBottom: 0 },
  },
  td: {
    height: tokens.density.rowComfortable,
    padding: `0 ${tokens.space[3]}`,
    fontSize: tokens.type.dense.size,
    color: tokens.color.text.secondary,
    borderBottom: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  strong: { color: tokens.color.text.primary, fontWeight: tokens.type.denseStrong.weight },
  subject: { color: tokens.color.text.primary },
  fileLink: {
    color: tokens.color.brand.primaryAction,
    fontWeight: tokens.type.denseStrong.weight,
    "$row:hover &": { textDecoration: "underline" },
  },
  dash: { color: tokens.color.text.disabled },
  status: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.space[1],
    height: "24px",
    padding: `0 ${tokens.space[2]}`,
    borderRadius: tokens.radius.full,
    fontSize: tokens.type.caption.size,
    fontWeight: tokens.type.denseStrong.weight,
    textTransform: "capitalize",
    backgroundColor: tokens.color.surface.subtle,
  },
  statusDot: {
    width: "6px",
    height: "6px",
    borderRadius: tokens.radius.full,
    flex: "0 0 auto",
  },
  statusActive: { color: tokens.color.status.success },
  statusActiveDot: { backgroundColor: tokens.color.status.success },
  statusInactive: { color: tokens.color.text.secondary },
  statusInactiveDot: { backgroundColor: tokens.color.text.disabled },
  actionsCell: {
    width: "44px",
    textAlign: "right",
    paddingRight: tokens.space[3],
  },
  chev: {
    color: tokens.color.text.disabled,
    opacity: 0,
    transform: "translateX(-3px)",
    transition: `all ${tokens.motion.fast} ${tokens.motion.easing}`,
    display: "inline-flex",
    "& svg": { fontSize: tokens.icon.default },
  },
}));

function CaseTable({ caseList }) {
  const classes = useStyle();
  const history = useHistory();

  const open = (fileNo) => history.push("/app/cases/" + fileNo + "/case");
  const val = (v) =>
    v === null || v === undefined || v === "" ? (
      <span className={classes.dash}>—</span>
    ) : (
      v
    );

  const StatusCell = ({ status }) => {
    if (!status) return val(status);
    const active = String(status).toLowerCase() === "active";
    return (
      <span
        className={`${classes.status} ${
          active ? classes.statusActive : classes.statusInactive
        }`}
      >
        <span
          className={`${classes.statusDot} ${
            active ? classes.statusActiveDot : classes.statusInactiveDot
          }`}
        />
        {status}
      </span>
    );
  };

  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th className={classes.th}>Client</th>
          <th className={classes.th}>Status</th>
          <th className={classes.th}>File #</th>
          <th className={classes.th}>Created At</th>
          <th className={classes.th}>Client File #</th>
          <th className={classes.th}>Due Date</th>
          <th className={classes.th}>Invoice Date</th>
          <th className={classes.th}>Primary Subject</th>
          <th className={classes.th}>Follow up</th>
          <th className={classes.th}>Event</th>
          <th className={classes.th}>User</th>
          <th className={classes.th} aria-hidden="true" />
        </tr>
      </thead>
      <tbody>
        {caseList.map((e, key) => (
          <tr
            className={classes.row}
            tabIndex={0}
            onClick={() => open(e.file_no)}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") open(e.file_no);
            }}
            key={key}
          >
            <td className={`${classes.td} ${classes.strong}`}>{val(e.client)}</td>
            <td className={classes.td}>
              <StatusCell status={e.status} />
            </td>
            <td className={`${classes.td} ${classes.fileLink}`}>{e.file_no}</td>
            <td className={classes.td}>{val(e.created_at)}</td>
            <td className={classes.td}>{val(e.client_file_no)}</td>
            <td className={classes.td}>{val(e.due_date)}</td>
            <td className={classes.td}>{val(e.invoice_date)}</td>
            <td className={`${classes.td} ${classes.subject}`}>
              {val(e.subject_name)}
            </td>
            <td className={classes.td}>{val(e.follow_up)}</td>
            <td className={classes.td}>{val(e.event)}</td>
            <td className={classes.td}>{val(e.user)}</td>
            <td className={`${classes.td} ${classes.actionsCell}`}>
              <span className={classes.chev}>
                <ChevronRightIcon />
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default CaseTable;
